import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, UserCheck, X, AlertCircle, ChevronDown } from 'lucide-react';
import { ROLE_LABELS } from '../../lib/constants';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui/Dropdown';
import { useLanguage } from '../../contexts/LanguageContext';

export default function ApproveProfileModal({ profile, users = [], onConfirm, onClose }) {
  const { t } = useLanguage();
  const creator = profile?.createdBy;
  const [assignMode, setAssignMode] = useState('creator'); // 'creator' | 'other'
  const [selectedUserId, setSelectedUserId] = useState(creator?.id || '');
  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Active users excluding clients/inactive
  const assignableUsers = (users || []).filter(u => u.is_active !== false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const targetAssigneeId = assignMode === 'creator' 
      ? (creator?.id || selectedUserId) 
      : selectedUserId;

    if (!targetAssigneeId) {
      setError('Please select an assignee for this profile.');
      return;
    }

    try {
      setIsSubmitting(true);
      await onConfirm({
        assignedToId: targetAssigneeId,
        remarks: remarks.trim() || undefined
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to approve profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg animate-scale-in border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{t('approveProfile') || 'Approve Profile'}</h3>
              <p className="text-xs text-gray-500">
                {profile?.clientName || 'Client Profile'}
                {profile?.projectName && ` • ${profile.projectName}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          {/* Creator Information */}
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200/70 text-xs text-gray-600 flex items-center justify-between">
            <div>
              <span className="text-gray-400 block font-medium">{t('submittedBy') || 'Submitted by:'}</span>
              <span className="font-semibold text-gray-800 text-sm">
                {creator?.username || 'Team Member'}
              </span>
              {creator?.role && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-200 text-gray-700 text-[10px] font-medium">
                  {ROLE_LABELS[creator.role] || creator.role}
                </span>
              )}
            </div>
          </div>

          {/* Assignee Selection */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
              <UserCheck className="w-3.5 h-3.5 text-blue-600" />
              {t('assignApprovedProfileTo') || 'Assign Approved Profile To:'}
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                assignMode === 'creator'
                  ? 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500 text-gray-900'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}>
                <input
                  type="radio"
                  name="assignMode"
                  value="creator"
                  checked={assignMode === 'creator'}
                  onChange={() => {
                    setAssignMode('creator');
                    if (creator?.id) setSelectedUserId(creator.id);
                  }}
                  className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                />
                <div className="text-xs">
                  <div className="font-semibold">{t('assignToCreator') || 'Assign to Creator'}</div>
                  <div className="text-gray-500 text-[11px] truncate">
                    {creator?.username || 'Creator'}
                  </div>
                </div>
              </label>

              <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                assignMode === 'other'
                  ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500 text-gray-900'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}>
                <input
                  type="radio"
                  name="assignMode"
                  value="other"
                  checked={assignMode === 'other'}
                  onChange={() => setAssignMode('other')}
                  className="mt-0.5 text-blue-600 focus:ring-blue-500"
                />
                <div className="text-xs">
                  <div className="font-semibold">{t('assignToSomeoneElse') || 'Assign to Someone Else'}</div>
                  <div className="text-gray-500 text-[11px]">
                    Select team member
                  </div>
                </div>
              </label>
            </div>

            {assignMode === 'other' && (
              <div className="pt-2 animate-fade-in">
                <Dropdown className="w-full">
                  <DropdownTrigger>
                    <div className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-white border rounded-xl text-sm shadow-sm cursor-pointer transition-all ${
                      selectedUserId ? 'border-blue-400 ring-1 ring-blue-400 text-gray-800' : 'border-gray-300 text-gray-400 hover:border-gray-400'
                    }`}>
                      <span className="truncate">
                        {selectedUserId
                          ? (() => { const u = assignableUsers.find(x => String(x.id) === String(selectedUserId)); return u ? `${u.username} (${ROLE_LABELS[u.role] || u.role})` : '-- Choose Team Member --'; })()
                          : '-- Choose Team Member --'
                        }
                      </span>
                      <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
                    </div>
                  </DropdownTrigger>
                  <DropdownPopover>
                    <DropdownMenu
                      selectionMode="single"
                      selectedKeys={selectedUserId ? new Set([String(selectedUserId)]) : new Set()}
                      onSelectionChange={(keys) => setSelectedUserId([...keys][0] || '')}
                    >
                      {assignableUsers.map((u) => (
                        <DropdownItem key={u.id} id={String(u.id)}>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-800">{u.username}</span>
                            <span className="text-[11px] text-gray-400">{ROLE_LABELS[u.role] || u.role}{u.email ? ` · ${u.email}` : ''}</span>
                          </div>
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
            )}
          </div>

          {/* Optional Approval Notes */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700">
              Approval Notes / Remarks (Optional):
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any instructions or remarks for the assignee..."
              rows={3}
              className="w-full px-3.5 py-2 text-sm bg-slate-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white outline-none transition-all resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              {t('cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm hover:shadow transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{t('saving') || 'Approving...'}</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{t('approve') || 'Approve & Assign'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
